import { motion } from 'framer-motion';
import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { cn, letterGenderById } from '../../utils/functions';

import { RegistrationLayout } from './components/RegistrationLayout';

import { StepWizard } from '@/components/StepWizard';
import useAuth from '@/hooks/useAuth';
import { usePermutive } from '@/hooks/usePermutive';
import ROUTES from '@/router/routes';
import {
  UserInitialData,
  AUTH0_NETWORKS,
  KeysOf,
  Maybe,
  Optional,
} from '@/types';
import { FADE_IN_OUT_LEFT, selectSocialNetwork } from '@/utils';
import { SECTION_NAME_FOR_ADS } from '@/utils/ads';

type DataPostRegisterFields = Maybe<
  Pick<
    UserInitialData,
    'nombre_dt' | 'nombre_equipo' | 'mail' | 'idSexo' | 'tiene_equipo'
  >
>;

export const Welcome = () => {
  const { userInitialData, user } = useAuth();

  const { trackPageView, isReady: isPermutiveReady } = usePermutive();

  useEffect(() => {
    // Permutive esta cargado
    if (isPermutiveReady) {
      trackPageView({
        section: SECTION_NAME_FOR_ADS.welcome,
        kw_premium: userInitialData?.premium,
        kw_edad: userInitialData?.edad_dt,
        kw_sexo: letterGenderById(userInitialData?.idSexo),
        kw_equipo: userInitialData?.club_hincha,
        kw_fecha: userInitialData?.fechaActiva.orden,
        kw_pines: userInitialData?.pinesDisponibles,
        kw_saldo: userInitialData?.saldo,
        kw_puntosporfecha: userInitialData?.puntos_fecha,
        kw_puntosportorneo: userInitialData?.puntos_general,
        kw_posicionporfecha: userInitialData?.posicion_fecha,
        kw_posicionportorneo: userInitialData?.posicion_general,
        kw_recibirinfo: userInitialData?.recibeInformacion,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPermutiveReady]);

  const loggingStrategy = user?.sub;

  const initialDataState = userInitialData as DataPostRegisterFields;
  const state = initialDataState;

  const normalizeStateDate = useMemo(() => {
    if (state) {
      return {
        name: state.nombre_dt,
        email: state.mail,
        teamName: state.nombre_equipo,
        idGender: parseInt(state.idSexo),
      };
    }

    return null;
  }, [state]);

  const [steps, setSteps] = useState(0);
  const navigate = useNavigate();

  if (initialDataState?.tiene_equipo) {
    navigate(ROUTES.myTeam11, {
      replace: true,
    });
  }

  const dataComponent = {
    subtitle:
      steps === 0
        ? 'Armá tu equipo con 11 titulares y 4 suplentes. Tenés $65.000.000 y podés elegir tantos jugadores por club como desees.'
        : 'El ganador de cada fecha del juego se lleva $1.500.000 siendo Premium y $100.000 si no lo es. El campeón de Gran DT gana $8.500.000 si es Premium y $500.000 si no lo es.',
    classSubtitle: `text-gdt-gray mt-2 mb-8 lg:mb-6 text-center font-medium leading-6 ${
      steps !== 0 && 'lg:mt-4 lg:mb-5'
    }`,
    nameContext:
      steps === 0
        ? 'welcome_1'
        : (('welcome_' + (steps + 1)) as 'welcome_1' | 'welcome_2'),
  };

  const classTitle = cn(
    `text-gdt-gray uppercase font-normal font-bebas text-3xl text-center leading-8`,
    {
      'lg:mt-[3.25rem]': steps !== 0,
    }
  );

  const handleSubmit = () => {
    navigate(ROUTES.buildTeam11, {
      replace: true,
      state: { access: 'FIRST_ACCESS' },
    });
  };

  const loggedSocialNetwork = loggingStrategy
    ?.split('|')
    .at(0)
    ?.toLocaleUpperCase() as Optional<KeysOf<typeof AUTH0_NETWORKS>>;

  const isNameToLong = (name: string | undefined) => {
    if (name) {
      return name.split(' ')[0].length >= 12;
    }
  };

  const welcomeByGender = useMemo(() => {
    const maleWord = 'Bienvenido';
    if (normalizeStateDate && normalizeStateDate.idGender) {
      if (normalizeStateDate.idGender === 1) {
        return maleWord;
      }

      return 'Bienvenida';
    }
  }, [normalizeStateDate]);

  /*
  useEffect(() => {
    trackFinRegistracion({
      ID: userInitialData?.pase_id,
      modo: userInitialData?.modoRegistracion,
    });
  }, []);
  */

  return (
    <RegistrationLayout
      className="lg:mt-[-2.7rem]"
      context={dataComponent.nameContext}
    >
      <StepWizard
        getCurrentStep={(activeStep: number) => setSteps(activeStep)}
        onSubmit={handleSubmit}
      >
        <motion.div
          key={steps}
          animate={'show'}
          initial="hide"
          variants={FADE_IN_OUT_LEFT}
        >
          <h1
            className={cn(classTitle, {
              'hyphens-auto': isNameToLong(normalizeStateDate?.name),
            })}
            lang="es"
          >
            ¡{welcomeByGender}, {normalizeStateDate?.name}!
            <br />
            Ya sos DT de {normalizeStateDate?.teamName}
          </h1>
          <p className={dataComponent.classSubtitle}>
            {dataComponent.subtitle}
          </p>
        </motion.div>
        <motion.div
          key={steps}
          animate={'show'}
          initial="hide"
          variants={FADE_IN_OUT_LEFT}
        >
          <h1 className={cn('lg:max-w-none max-w-[12rem] m-auto', classTitle)}>
            ¡PODÉS GANAR MUCHOS PREMIOS!
          </h1>
          <p className={dataComponent.classSubtitle}>
            {dataComponent.subtitle}
          </p>
        </motion.div>
      </StepWizard>
      <p className="text-sm text-gdt-gray-3 font-semibold text-center">
        Recordá que ingresás con tu cuenta{' '}
        <span className="block" role="separator" /> de{' '}
        {selectSocialNetwork(loggedSocialNetwork)}{' '}
        <strong className="text-black"> {normalizeStateDate?.email}</strong>
      </p>
    </RegistrationLayout>
  );
};
